import React, { useEffect, useState } from 'react';
import FormItem from '@/components/molecules/formItem';
import { cn } from '@/lib/utils';

const convertToPlain = (html) => {
	const tempDivElement = document.createElement('div');
	tempDivElement.innerHTML = html;
	return tempDivElement.textContent || tempDivElement.innerText || '';
};
const isJsonString = (str) => {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
};

/**
 * @typedef {Object} Fields
 * @property {string} address_1
 * @property {string} address_2
 * @property {string} town
 * @property {string} county
 * @property {string} postcode
 */

/**
 * @typedef {Object} AddressLookupProps
 * @property {function} setData
 * @property {Object} errors
 * @property {Object} data
 * @property {Fields} [fields]
 * @property {string} [className]
 */

/**
 * @name AddressLookup
 * @description Renders a form to lookup an address
 * @param {AddressLookupProps} props
 * @returns {React.JSX.Element}
 * @example <AddressLookup setData={setData} errors={errors} data={data} fields={fields} />
 */

function AddressLookup({ data, setData, errors, fields, onChange, postcodeField, inertiaData, className, defaultFullAddress, postcodeData }) {
	let initialFullAddress = defaultFullAddress;
	// test if the data is a json string
	if (isJsonString(inertiaData?.[0]?.[postcodeField?.key])) {
		Object.keys(fields).forEach((fk) => {
			initialFullAddress = {
				...initialFullAddress,
				[fk]: inertiaData?.[0]?.[postcodeField?.key] ? JSON.parse(inertiaData[0]?.[postcodeField?.key])[fk] : data?.[fk],
			};
		});
	}

	const [fullAddress, setFullAddress] = useState(initialFullAddress);

	const onPostcodeSelect = (values) => {
		if (!values) return;

		Object.keys(fields).forEach((item) => {
			setFullAddress((prevState) => ({
				...prevState,
				...{
					[item]:
						fields[item].key === 'Line1' && values[fields[item].optional] !== '' && fields[item]?.optional
							? values[fields[item].optional] + ', ' + values[fields[item].key]
							: values[fields[item].key ?? ''],
				},
			}));
		});
	};

	useEffect(() => {
		if (setData) {
			const newData = {};
			Object.keys(fields).forEach((fk) => {
				newData[fk] = fullAddress[fk];
			});
			setData((prev) => ({
				...prev,
				...newData,
			}));
		}

		onChange && onChange(JSON.stringify(fullAddress));
	}, [fullAddress]);

	return (
		<div className={cn('w-full flex flex-col gap-6', className)}>
			<FormItem
				type={postcodeField.type}
				name={`${postcodeField.label.toLowerCase().replace(' ', '-')}_${postcodeField.type}`}
				label="Postcode Lookup"
				placeholder="Enter your Postcode..."
				validation={postcodeField.required ? 'required' : null}
				value={postcodeData}
				onChange={onPostcodeSelect}
				{...(postcodeField.max_length > 0 && { description: `${postcodeField.max_length} characters recommended.` })}
			/>

			{Object.keys(fields).map((input_key, idx) => {
				const inputEl = fields[input_key];

				return (
					<FormItem
						key={`${postcodeField.key}_${inputEl.label.toLowerCase().replaceAll(' ', '-')}_text_${idx}`}
						type="text"
						label={inputEl.label}
						name={`${postcodeField.key}_${inputEl.label.toLowerCase().replaceAll(' ', '-')}_text`}
						placeholder="Enter details"
						value={setData ? data[input_key] : fullAddress[input_key]}
						onChange={(value) => {
							setFullAddress((prevState) => ({
								...prevState,
								[input_key]: value,
							}));

							if (setData) {
								setData((prev) => ({
									...prev,
									[input_key]: value,
								}));
							}
						}}
						validation={input_key === 'address_1' || input_key === 'town' || input_key === 'postcode' ? 'required' : null}
						meta={{
							msg:
								postcodeField.group_field_id && errors?.[postcodeField.group_field_id]
									? errors?.[postcodeField.group_field_id]?.[input_key]
									: errors?.[input_key],
							touched: errors?.[postcodeField.group_field_id]?.[input_key] || errors?.[input_key] ? true : false,
						}}
					/>
				);
			})}
		</div>
	);
}

AddressLookup.defaultProps = {
	defaultFullAddress: {},
	fields: {
		address_1: { key: 'Line1', label: 'Address Line 1' },
		address_2: { key: 'Line2', label: 'Address Line 2' },
		town: { key: 'City', label: 'Town' },
		county: { key: 'Province', label: 'County' },
		postcode: { key: 'PostalCode', label: 'Postcode' },
	},
};
export default AddressLookup;
